import React from 'react';

import {trackArtifactsEvent} from '../../services/analytics';
import {makeTrackEvent} from '../../services/analytics/makeTrackEvent';
import {recordRumAction} from '../../util/datadog/datadogRum';

export type CollectionOverviewAnalyticsContextValue = {
  artifactCollectionId: string;
  artifactCollectionName: string;
  artifactTypeName: string;
  collectionType: 'portfolio' | 'sequence';
  entityName: string;
  projectName: string;
  source: 'registry' | 'artifacts';
};

type CollectionOverviewViewedEvent = {
  artifactCollectionName: string;
  artifactTypeName: string;
  entityName: string;
  projectName: string;
  source: 'registry' | 'artifacts';
};

/**
 * Dedicated Segment event (same pattern as Artifact Lineage Viewed).
 * Prefer this over perf_metrics customer_ready for product view counts —
 * Segment overwrites fine-grained pageName with the route pageGroup.
 */
const trackCollectionOverviewViewedEvent = makeTrackEvent<
  CollectionOverviewViewedEvent,
  {
    artifactCollectionName: {
      description: 'Name of the artifact collection whose overview was viewed.';
      exampleValues: ['my-collection'];
    };
    artifactTypeName: {
      description: 'Artifact type for the collection overview.';
      exampleValues: ['model', 'dataset'];
    };
    entityName: {
      description: 'Entity owning the artifact collection.';
      exampleValues: ['<name>'];
    };
    projectName: {
      description: 'Project owning the artifact collection.';
      exampleValues: ['core-project'];
    };
    source: {
      description: 'Product surface where the overview was opened.';
      exampleValues: ['registry', 'artifacts'];
    };
    _description: 'Tracks when an Artifact Collection Overview card is ready to view.';
    _location: 'Artifact Collection Overview';
    _motivation: 'Provide an Overview-only views denominator for product analytics.';
  }
>('Artifact Collection Overview Viewed');

export type CollectionOverviewActionDetail =
  | 'edit_overview'
  | 'delete_draft'
  | 'delete_overview'
  | 'generate_draft'
  | 'ai_generate_draft'
  | 'ai_generate_draft_succeeded'
  | 'ai_generate_draft_failed'
  | 'publish_overview'
  | 'close_without_publishing'
  | 'add_artifact_versions_grid'
  | 'add_panel'
  | 'configure_panel'
  | 'edit_panel'
  | 'delete_panel'
  | 'search_version';

export const CollectionOverviewAnalyticsContext =
  React.createContext<CollectionOverviewAnalyticsContextValue | null>(null);

export function trackCollectionOverviewInteraction(
  context: CollectionOverviewAnalyticsContextValue | null | undefined,
  actionDetail: CollectionOverviewActionDetail,
  additionalData: Record<string, unknown> = {}
) {
  if (context == null) {
    return;
  }

  trackArtifactsEvent({
    action: 'collection_overview_interaction',
    artifactCollectionName: context.artifactCollectionName,
    artifactTypeName: context.artifactTypeName,
    entityName: context.entityName,
    projectName: context.projectName,
    additionalData: {
      actionDetail,
      artifactCollectionId: context.artifactCollectionId,
      collectionType: context.collectionType,
      source: context.source,
      ...additionalData,
    },
  });
}

export function trackCollectionOverviewViewed(
  context: CollectionOverviewAnalyticsContextValue
) {
  const payload: CollectionOverviewViewedEvent = {
    artifactCollectionName: context.artifactCollectionName,
    artifactTypeName: context.artifactTypeName,
    entityName: context.entityName,
    projectName: context.projectName,
    source: context.source,
  };

  trackCollectionOverviewViewedEvent(payload);
  recordRumAction('Artifacts.collection_overview_viewed', payload);
}

export function useCollectionOverviewAnalyticsContext() {
  return React.useContext(CollectionOverviewAnalyticsContext);
}

export function useTrackCollectionOverviewVersionSearch({
  searchTerm,
  analyticsContext,
  gridId,
  selectedVersionCount,
}: {
  searchTerm: string;
  analyticsContext: CollectionOverviewAnalyticsContextValue | null | undefined;
  gridId?: string;
  selectedVersionCount: number;
}) {
  const trimmedSearchTerm = searchTerm.trim();
  const previousTrackedSearchRef = React.useRef('');

  React.useEffect(() => {
    if (
      trimmedSearchTerm === '' ||
      previousTrackedSearchRef.current === trimmedSearchTerm
    ) {
      return;
    }

    previousTrackedSearchRef.current = trimmedSearchTerm;
    trackCollectionOverviewInteraction(analyticsContext, 'search_version', {
      gridId,
      hasSearchTerm: true,
      searchLength: trimmedSearchTerm.length,
      selectedVersionCount,
    });
  }, [analyticsContext, gridId, selectedVersionCount, trimmedSearchTerm]);
}

export function useTrackCollectionOverviewGridAdded({
  analyticsContext,
  isNewlyCreatedGrid,
  gridId,
  panelCount,
  selectedVersionCount,
}: {
  analyticsContext: CollectionOverviewAnalyticsContextValue | null | undefined;
  isNewlyCreatedGrid: boolean;
  gridId?: string;
  panelCount: number;
  selectedVersionCount: number;
}) {
  const trackedGridIdsRef = React.useRef(new Set<string>());

  React.useEffect(() => {
    if (
      !isNewlyCreatedGrid ||
      gridId == null ||
      gridId === '' ||
      trackedGridIdsRef.current.has(gridId)
    ) {
      return;
    }

    trackedGridIdsRef.current.add(gridId);
    trackCollectionOverviewInteraction(
      analyticsContext,
      'add_artifact_versions_grid',
      {
        gridId,
        panelCount,
        selectedVersionCount,
      }
    );
  }, [
    analyticsContext,
    gridId,
    isNewlyCreatedGrid,
    panelCount,
    selectedVersionCount,
  ]);
}
